import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";

/**
 * `will-change: transform` est ici l'un des rares cas où la promotion permanente
 * se justifie : la rotation ne s'arrête jamais tant que le spinner est monté,
 * donc la couche promue sert à chaque image de sa vie plutôt que d'occuper de la
 * mémoire vidéo pour rien.
 */
const spinnerVariants = cva(
  "inline-block shrink-0 transform-gpu will-change-transform animate-spin rounded-full",
  {
    variants: {
      size: {
        xs: "size-3",
        sm: "size-4",
        md: "size-5",
        lg: "size-8",
      },
    },
    defaultVariants: { size: "sm" },
  },
);

/** L'épaisseur suit la taille : un anneau de 3px sur 12px de diamètre serait un disque. */
const THICKNESS = { xs: "2px", sm: "2px", md: "2.5px", lg: "3px" } as const;

export interface SpinnerProps
  extends Omit<ComponentPropsWithRef<"span">, "children">,
    VariantProps<typeof spinnerVariants> {
  /** Texte lu par les lecteurs d'écran. Passez `null` si un texte voisin décrit déjà l'attente. */
  label?: string | null;
}

/**
 * Indicateur d'attente indéterminée.
 *
 * Anneau à opacité DÉGRADÉE plutôt qu'arc plein : un dégradé conique fournit la
 * couleur, un masque radial y perce le centre. La densité décroît continûment le
 * long du cercle, ce qui donne une rotation lisse et une traînée naturelle —
 * là où un arc plein montre une extrémité franche qui saute à chaque tour.
 *
 * La technique est aussi plus légère qu'un SVG : un seul élément, aucun tracé.
 * Il hérite de `currentColor`, donc se teinte du texte environnant.
 */
export function Spinner({ className, size = "sm", label = "Chargement", style, ...props }: SpinnerProps) {
  const thickness = THICKNESS[size ?? "sm"];
  // Le masque évide le disque pour n'en garder qu'un anneau de `thickness`.
  const mask = `radial-gradient(farthest-side, transparent calc(100% - ${thickness}), #000 calc(100% - ${thickness}))`;

  return (
    <span
      // `role="status"` + le texte masqué suffisent : l'apparition du spinner
      // est déjà annoncée par le changement de contenu de la région.
      role={label ? "status" : undefined}
      aria-hidden={label ? undefined : true}
      // Seule exception au respect de `prefers-reduced-motion` : un indicateur
      // d'attente figé ne signale plus rien. La rotation est une information.
      data-motion-essential=""
      className={cn(spinnerVariants({ size }), className)}
      style={{
        background: "conic-gradient(from 0deg, transparent 5%, currentColor 95%)",
        WebkitMask: mask,
        mask,
        ...style,
      }}
      {...props}
    >
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}

export { spinnerVariants };
